/* ----------------------------------------------------------------------------------------------
   MS CART RECOMMENDATIONS
   ----------------------------------------------------------------------------------------------
   Fills the cart drawer's upsell shelf with Shopify's own product recommendations (intent
   'related', driven by real store data) in place of a hand-picked list.

   Shape of the thing:
     - sections/cart-drawer.liquid renders the shelf server-side with the manual picks already
       in it, and stamps the seed product id on [data-ms-recs].
     - this script asks Shopify for live picks and swaps them in ONLY when the answer has cards.
     - an empty answer, a network failure or a blocked fetch leaves the manual shelf untouched.
   So the shelf is never empty, never flashes, and a dead API is invisible to the shopper.

   Why the fetch targets a separate section: the drawer lives in a section group and section
   groups cannot be re-rendered standalone, so the shelf cannot fetch into itself.
   sections/cart-recommendations.liquid is the render target. See docs/06-gotchas.md.

   The drawer redraws itself from the server on cart:refresh (the theme's native path, also used
   by ms-gift-ladder.js), which restores the manual shelf and a fresh seed. We re-ask after that
   redraw lands, twice, because the redraw has no completion event.
---------------------------------------------------------------------------------------------- */
(function () {
  'use strict';

  var SECTION = 'cart-recommendations';
  var INTENT = 'related';
  var ASK = 10; /* over-fetch: the render target drops cart items, wholesale SKUs and add-ons */
  var RETRIES = [300, 900]; /* match ms-gift-ladder.js's repaint window */

  /* On any domain that is not the primary one, Shopify 301s storefront HTML to matchasunday.com.
     A cross-origin redirect makes fetch() throw, so the shelf silently never fills. Production is
     same-origin and unaffected, but every *.myshopify.com draft preview would look broken, which
     is exactly the trap in docs/06-gotchas.md. _fd=0 suppresses the domain redirect and _ab=0 the
     A/B assignment; both are no-ops on the live domain. */
  var NO_REDIRECT = '&_fd=0&_ab=0';

  var cache = {}; /* seed id -> html, so reopening the drawer costs nothing */
  var inflight = {};

  function shelves() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-ms-recs]'));
  }

  /* Swap in Shopify's cards. Bails out without touching anything if the answer is empty, which
     is what keeps the manual picks working as the safety net. */
  function fill(shelf, html) {
    var target = shelf.querySelector('[data-ms-recs-target]');
    if (!target) return;

    var fresh;
    try {
      fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ms-recs-list]');
    } catch (e) {
      return;
    }
    if (!fresh) return;

    var cards = fresh.querySelectorAll('.horizontal-product');
    if (!cards.length) return;

    /* Only take over when Shopify can fill the shelf at least as well as what is already there.
       Wholesale is why: the engine is thin on that range and has handed back a single product
       where the curated backup list had four, which read as a downgrade. Retail is unaffected,
       its backup list is empty so anything Shopify returns wins. */
    var existing = target.querySelectorAll('.horizontal-product').length;
    if (cards.length < existing) return;

    target.innerHTML = fresh.innerHTML;
    shelf.setAttribute('data-ms-recs-source', 'shopify');

    /* ms-case-upgrade.js sets this when a wholesale case upgrade is on screen, so a cafe sees
       one offer rather than two. Honour it: unhiding here would undo that, and which of the two
       scripts finishes last is pure timing. */
    if (!shelf.hasAttribute('data-ms-recs-suppressed')) {
      shelf.removeAttribute('hidden');
    }

    /* Arrows are pointless with a single card, and the carousel does not hide them itself. */
    var arrows = shelf.querySelector('[data-ms-recs-arrows]');
    if (arrows) {
      if (cards.length > 1) arrows.removeAttribute('hidden');
      else arrows.setAttribute('hidden', '');
    }
  }

  function load(shelf) {
    var seed = shelf.getAttribute('data-ms-recs');
    if (!seed) return;
    if (shelf.getAttribute('data-ms-recs-source') === 'shopify') return;

    if (cache[seed]) {
      fill(shelf, cache[seed]);
      return;
    }

    if (!inflight[seed]) {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var url =
        root +
        'recommendations/products?product_id=' +
        encodeURIComponent(seed) +
        '&limit=' +
        ASK +
        '&intent=' +
        INTENT +
        '&section_id=' +
        SECTION +
        NO_REDIRECT;

      inflight[seed] = fetch(url, { priority: 'low' })
        .then(function (r) {
          if (!r.ok) throw new Error(r.status);
          return r.text();
        })
        .then(function (html) {
          cache[seed] = html;
          delete inflight[seed];
          return html;
        })
        .catch(function (e) {
          delete inflight[seed];
          throw e;
        });
    }

    inflight[seed]
      .then(function (html) {
        fill(shelf, html);
      })
      .catch(function () {
        /* the manual shelf is already correct and already on screen; stay quiet */
      });
  }

  function run() {
    shelves().forEach(load);
  }

  function runAfterRedraw() {
    RETRIES.forEach(function (ms) {
      setTimeout(run, ms);
    });
  }

  document.addEventListener('cart:refresh', runAfterRedraw);
  document.addEventListener('cart:change', runAfterRedraw);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
